import React, { useCallback, useEffect, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import { SelectableItem, SelectableList } from '../../../components/SelectableList';
import { Tabs } from '../../../components/Tabs';
import MyAppText from '../../../components/MyAppText';
import { MyCheckbox } from '../../../components/MyCheckbox';
import { accessService } from '../../../services/central-api/access';
import MyDropDownPicker from '../../../components/MyDropDownPicker';
import { cameraService } from '../../../services/central-api/cameras';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import ActingBodiesDropdown from '../../../components/access/ActingBodiesDropdown';
import Toast from 'react-native-toast-message';
import { NavigationProp, RouteProp, useFocusEffect, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import CustomButton from '../../../components/CustomButton';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import Container from '../../../components/Container';
import { useAuth } from '../../../context/Auth';
import ActingBodiesUnitsDropdown from '../../../components/Dropdowns/ActingBodiesUnitsDropdown';
import { AccessParamList } from '../../../typings/Params';

const defaultPermissionsValue: GroupPermissions = {
    camera_watch: true,
    camera_watch_timeline: false,
    camera_move_ptz: false,
    camera_watch_facial_detections: false,
    camera_watch_lpr_detections: false
};

export const groupPermissionsStructure: { title: string; values: { label: string; value: keyof GroupPermissions; }[]; }[] = [
    {
        title: 'cameras',
        values: [
            { label: 'canWatchLive', value: 'camera_watch' },
            { label: 'canWatchRecords', value: 'camera_watch_timeline' },
            { label: 'canMovePtz', value: 'camera_move_ptz' },
            { label: 'canWatchFacialDetections', value: 'camera_watch_facial_detections' },
            { label: 'canWatchLprDetections', value: 'camera_watch_lpr_detections' },
        ],
    },
];

export default function GroupForm() {
    const navigation = useNavigation<NavigationProp<AccessParamList>>();
    const route = useRoute<RouteProp<AccessParamList, 'GroupForm'>>();
    const groupId = route.params?.id;
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    const [actingBodyId, setActingBodyId] = useState<number>(0);
    const [actingBodyUnitId, setActingBodyUnitId] = useState<number>(0);

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [selectableUsers, setSelectableUsers] = useState<SelectableItem[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<SelectableItem[]>([]);
    const [groupUserIds, setGroupUserIds] = useState<number[]>([]);
    const [selectableCameras, setSelectableCameras] = useState<SelectableItem[]>([]);
    const [selectedCameras, setSelectedCameras] = useState<SelectableItem[]>([]);
    const [groupCameraIds, setGroupCameraIds] = useState<string[]>([]);
    const [selectableTagsFilter, setSelectableTagsFilter] = useState<{ label: string; value: number; }[]>([]);
    const [isTagPickerOpen, setIsTagPickerOpen] = useState(false);
    const [selectedTags, setSelectedTags] = useState<number[]>([]);
    const [cameras, setCameras] = useState<AccessSimplifiedCameraData[]>([]);
    const [displayFilter, setDisplayFilter] = useState<string[]>([]);
    const [permissions, setPermissions] = useState<GroupPermissions>(defaultPermissionsValue);

    function cleanValues() {
        setName('');
        setDescription('');
        setSelectedUsers([]);
        setSelectableUsers([]);
        setActingBodyId(0);
        setActingBodyUnitId(0);
        setSelectableCameras([]);
        setSelectedCameras([]);
        setSelectedTags([]);
        setCameras([]);
        setPermissions(defaultPermissionsValue);
    }

    async function getGroup(id: number, cameras: AccessSimplifiedCameraData[]) {
        const group = await accessService.getGroup(id);
        setName(group.name);
        setDescription(group.description);
        setPermissions(group.groupPermissionObj);
        if (group.actingBodyUnit?.actingBodyId) {
            setActingBodyId(group.actingBodyUnit.actingBodyId);
            setActingBodyUnitId(group.actingBodyUnitId);

            const users = await loadUsers(group.actingBodyUnit.id || 0);
            const selectedUsers = group.Users.map((user) => {
                return {
                    id: user.id || 0,
                    name: user.name
                };
            });
            setSelectedUsers(selectedUsers);

            setSelectableUsers((users || [])
                .filter((user) => !selectedUsers.find((groupUser) => groupUser.id == user.id))
                .map((user) => {
                    return {
                        id: user.id || 0,
                        name: user.name
                    };
                })
            );

            setGroupUserIds(group.Users.map((user) => user.id || 0));
        }


        const selectedCameras = group.Cameras.map((camera) => {
            return {
                id: camera.id,
                name: camera.title
            };
        });
        setSelectedCameras(selectedCameras);
        setGroupCameraIds(group.Cameras.map((camera) => camera.id));
        setSelectableCameras(cameras
            .filter((camera) => !selectedCameras.find((groupCamera) => groupCamera.id == camera.id))
            .map((camera) => {
                return {
                    id: camera.id,
                    name: camera.title
                };
            })
        );
    }

    async function loadPageInfo(authenticatedUser: AuthenticatedUser) {
        try {
            const [cameras, tags] = await Promise.all([
                accessService.getAllCameras({}),
                cameraService.getCameraTagsSimplified(),
            ]);

            setSelectableTagsFilter((tags || []).map((tag) => {
                return {
                    value: tag.id,
                    label: tag.name
                };
            }));

            setCameras(cameras);
            setDisplayFilter(cameras.map(camera => {
                return camera.id;
            }));

            if (groupId) {
                await getGroup(groupId, cameras);
            } else {
                setSelectableCameras(cameras.map((camera) => {
                    return {
                        id: camera.id || 0,
                        name: camera.title
                    };
                }));

                if (!(authenticatedUser.isAdmin
                    || authenticatedUser.permissions.access_edit_group_settings_all_acting_bodies) && authenticatedUser.actingBodyUnitId) {
                    setActingBodyId(authenticatedUser.role?.actingBody?.id || 0);
                    if (!authenticatedUser.permissions.access_edit_group_settings_acting_body_unit) {
                        setActingBodyUnitId(authenticatedUser.actingBodyUnitId);
                    }
                }
            }
        } catch (err) {
            console.error(err);
        }
    }

    async function loadUsers(actingBodyUnitId: number) {
        try {
            const users = await accessService.getAccessAllUsers({ actingBodyUnitId: actingBodyUnitId });
            return users;
        } catch (err) {
            console.error(err);
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused && authenticatedUser) {
            loadPageInfo(authenticatedUser);
        }
    }, [isFocused, navigation, route.params, authenticatedUser]));

    useEffect(() => {
        if (!selectableTagsFilter || !selectedTags || !selectableCameras || !selectedCameras || !cameras) {
            return;
        }

        if (selectedTags.length == 0) {
            return setDisplayFilter(cameras.map(camera => {
                return camera.id;
            }));
        }

        setDisplayFilter(cameras.filter((camera: AccessSimplifiedCameraData) => {
            return camera.tagList?.some(tag => selectedTags.includes(tag));
        }).map(camera => {
            return camera.id;
        }));
    }, [selectedTags]);

    function isEmpty(value: string) {
        return value == '';
    }

    function isFormValid() {
        if (((authenticatedUser?.isAdmin
            || authenticatedUser?.permissions.access_edit_group_settings_all_acting_bodies
            || authenticatedUser?.permissions.access_edit_group_settings_acting_body_unit)
            && actingBodyUnitId === 0)) {
            return false;
        }

        return !isEmpty(name);
    }

    async function formSubmit() {
        try {
            if (groupId) {
                const selectedUserIds = selectedUsers.map((user) => Number(user.id));
                const usersToAdd = selectedUserIds.filter((selectedUserId) => !groupUserIds.includes(selectedUserId));
                const usersToRm = groupUserIds.filter((groupUserId) => !selectedUserIds.includes(groupUserId));

                const selectedCameraIds = selectedCameras.map((camera) => String(camera.id));
                const camerasToAdd = selectedCameraIds.filter((selectedCameraId) => !groupCameraIds.includes(selectedCameraId));
                const camerasToRm = groupCameraIds.filter((groupCameraId) => !selectedCameraIds.includes(groupCameraId));

                await accessService.updateGroup(groupId, {
                    name,
                    description,
                    usersToAdd,
                    usersToRm,
                    camerasToAdd,
                    camerasToRm,
                    permissions
                });
            } else {
                await accessService.createGroup({
                    name,
                    description,
                    usersToAdd: selectedUsers.map(item => Number(item.id)),
                    camerasToAdd: selectedCameras.map(item => String(item.id)),
                    permissions,
                    unitId: actingBodyUnitId,
                });
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            cleanValues();
            navigation.navigate('Groups', {
                page: 1,
                limit: 25,
            });
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    async function changedUnit(unitId: number) {
        const users = await loadUsers(unitId);

        setSelectableUsers((users || []).map((user) => {
            return {
                id: user.id || 0,
                name: user.name
            };
        }));
    }

    useEffect(() => {
        if (actingBodyId !== 0) {
            setSelectedUsers([]);
            setSelectableUsers([]);
        }

        if (actingBodyUnitId !== 0 && !groupId) {
            changedUnit(actingBodyUnitId);
        }
    }, [actingBodyId, actingBodyUnitId]);

    if (!authenticatedUser) {
        return <></>;
    }

    return (
        <Container>
            <Header
                goBackAction={() => {
                    cleanValues();
                    navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Groups', {
                        page: 1,
                        limit: 25,
                    });
                }}
                title={translate('registerGroup')}
                actions={<CustomButton
                    text={translate('save')}
                    disabled={!isFormValid()}
                    onPress={formSubmit}
                />}
            />
            <Card>
                <Tabs tabs={[{
                    key: 'details',
                    label: translate('details')
                }, {
                    key: 'users',
                    label: translate('users')
                }, {
                    key: 'cameras',
                    label: translate('cameras')
                }, {
                    key: 'permissions',
                    label: translate('permissions')
                }]}>
                    {({ selectedTab }) => {
                        if (selectedTab == 'details') {
                            return (<ScrollView contentContainerStyle={styles.detailsContainer}>
                                {(authenticatedUser.isAdmin || authenticatedUser.permissions.access_edit_group_settings_all_acting_bodies) &&
                                    <ActingBodiesDropdown
                                        value={actingBodyId}
                                        setValue={setActingBodyId}
                                        zIndex={3}
                                        disabled={!!groupId}
                                        includeAllOption={false}
                                        required={true}
                                    />
                                }
                                {(authenticatedUser?.isAdmin
                                    || authenticatedUser?.permissions.access_edit_group_settings_all_acting_bodies
                                    || authenticatedUser?.permissions.access_edit_group_settings_acting_body_unit) &&
                                    <ActingBodiesUnitsDropdown
                                        actingBodyId={actingBodyId}
                                        value={actingBodyUnitId}
                                        setValue={setActingBodyUnitId}
                                        zIndex={2}
                                        disabled={!!groupId}
                                        required={true}
                                    />
                                }
                                <FormInput
                                    label={translate('name')}
                                    placeholder={translate('name')}
                                    value={name}
                                    onChangeText={setName}
                                    invalid={isEmpty}
                                    required={true}
                                />
                                <FormInput
                                    label={translate('description')}
                                    placeholder={translate('description')}
                                    value={description}
                                    onChangeText={setDescription}
                                    invalid={() => false}
                                />
                            </ScrollView>);
                        } else if (selectedTab == 'users') {
                            return <SelectableList
                                selectableLabel={translate('users')}
                                selectableContent={selectableUsers}
                                selectedLabel={translate('participants')}
                                selectedContent={selectedUsers}
                            />;
                        } else if (selectedTab == 'cameras') {
                            return (<View style={{ rowGap: 10, flex: 1 }}>
                                <View style={{ zIndex: 2 }}>
                                    <MyAppText>{translate('tags')}</MyAppText>
                                    <MyDropDownPicker
                                        placeholderColor={theme.colors.placeholder}
                                        placeholder={translate('filter')}
                                        open={isTagPickerOpen}
                                        items={selectableTagsFilter}
                                        setOpen={setIsTagPickerOpen}
                                        value={selectedTags}
                                        setValue={setSelectedTags}
                                        multiple={true}
                                        mode='BADGE'
                                        searchable={true}
                                    />
                                </View>
                                <SelectableList
                                    displayFilter={displayFilter}
                                    selectableLabel={translate('cameras')}
                                    selectableContent={selectableCameras}
                                    selectedLabel={translate('participants')}
                                    selectedContent={selectedCameras}
                                />
                            </View>);
                        } else if (selectedTab == 'permissions') {
                            return (
                                <View style={{ gap: 4 }}>
                                    {groupPermissionsStructure.map((permission, index) => (
                                        <View key={index} style={{ gap: 4 }}>
                                            <MyAppText style={{ fontWeight: 'bold' }}>{translate(permission.title)}</MyAppText>
                                            {permission.values.map((permissionValue) => (
                                                <MyCheckbox
                                                    key={permissionValue.value}
                                                    label={translate(permissionValue.label)}
                                                    checked={permissions[permissionValue.value]}
                                                    setChecked={(value) => {
                                                        setPermissions((prevPermissions) => ({
                                                            ...prevPermissions,
                                                            [permissionValue.value]: value,
                                                        }));
                                                    }}
                                                />
                                            ))}
                                        </View>
                                    ))}
                                </View>);
                        }
                    }}
                </Tabs>
            </Card>
        </Container>
    );
}

const styleSheet = createStyleSheet(() => ({
    detailsContainer: {
        flex: 1,
        flexDirection: 'column',
        gap: 16,
    },
}));
